import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, } from 'react-router-dom';
import { SERVICE_CATEGORY, SERVICE_MODE, SERVICE_TYPE_KEY, STATUS_TYPES, URL_PATH } from '../../constants';
import { IExceptionModel, IExceptionRequest, IServiceRequestModel } from '../../models';
import { AppDispatch, useSelector } from '../../store/AppStore';
import { createServiceRequest } from '../../store/actions/sagaActions/services/CreateServiceSagaActions';
import { setShowServiceExistWarning } from '../../store/reducer/services/ServiceOperationMonitorReducer';
import { prefixNamespaceMapper, serviceOperationMapper } from '../../utilities/services/ServiceListUtils';

export const useCreateService = (
  success: (title: string, message: string) => void,
  failure: (title: string, message: string) => void,
  warning: (title: string, message: string) => void,
  setViewStatus: (viewStatus: boolean) => void,
  setIsEdit: (isEdit: boolean) => void,
  viewStatus: boolean
) => {
  const [isCreateServiceLoading, setIsCreateServiceLoading] = useState(false);
  // Store data
  const { serviceOperations } = useSelector((state) => state.serviceOperationReducer);
  const { serviceName, serviceDescription, endPoint, serviceType, authDetails: reducerAuthDetails, soapAttributes: reducerSoapAttributes } = useSelector((state) => state.serviceDetailsReducer);
  const { serviceData, success: createServiceSuccess, errorMessage: createServiceErrorMsg } = useSelector((state) => state.serviceReducer);
  const { ingressEventMappingList } = useSelector((state) => state.ingressEventMappingReducer);
  const { egressEventMappingList } = useSelector((state) => state.egressEventMappingReducer);
  const { exceptions: exceptionList } = useSelector((state) => state.exceptionReducer);
  // Hooks
  const history = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const location = useLocation();
  const createAuthDetailsRequest = () => ({
    authType: reducerAuthDetails?.authType,
    username: reducerAuthDetails?.username,
    password: reducerAuthDetails?.password,
    authMethod: reducerAuthDetails?.authMethod,
    authUrl: reducerAuthDetails?.authUrl,
    headerParam: reducerAuthDetails?.headerParam ?? {},
    requestParam: reducerAuthDetails?.requestParam ?? {},
    queryPathParam: reducerAuthDetails?.queryPathParam ?? {},
  });

  /**
   * Creates soap attributes
   * @returns soap attributes
   */
  const createSoapAttributes = () => ({
    headersXML: reducerSoapAttributes?.headersXML,
    envelopeNamespaces: reducerSoapAttributes?.envelopeNamespaces ? prefixNamespaceMapper(reducerSoapAttributes?.envelopeNamespaces) : [],
  });

  /**
  * @method callCreateService
  * @description calls the create service api
  */
  const callCreateService = async (): Promise<void> => {
    setIsCreateServiceLoading(true);
    // construct the exception model
    const requestExceptionList: IExceptionRequest[] = exceptionList.map((exception: IExceptionModel) => ({
      ersExceptionName: exception.exceptionId,
      errorCode: `${exception.errorCode}`,
      actions: exception.action
    }));

    const createServiceData: IServiceRequestModel = {
      serviceName,
      serviceDescription,
      serviceCategory: SERVICE_CATEGORY,
      endPoint,
      serviceType,
      authDetails: reducerAuthDetails?.authType ? createAuthDetailsRequest() : null,
      soapAttributes: serviceType === SERVICE_TYPE_KEY.SOAP ? createSoapAttributes() : null,
      serviceOperations: serviceOperations ? serviceOperationMapper(serviceOperations, ingressEventMappingList, egressEventMappingList) : [],
      serviceExceptions: requestExceptionList
    };
    dispatch(createServiceRequest(createServiceData));
  };

  /**
  * Displays the success toast for create service
  */
  const showCreateServiceSuccessToast = () => {
    success(
      translate('toast_component.lbl_success'),
      translate('service_wrapper_component.msg_create_service_success_body', {
        replace: { serviceName: serviceData?.serviceName }
      })
    );
  };

  useEffect(() => {
    if (createServiceSuccess) {
      setViewStatus(true);
      setIsEdit(false);
      showCreateServiceSuccessToast();
    } else if (createServiceErrorMsg) {
      failure(
        translate('toast_component.lbl_error'),
        translate('service_wrapper_component.msg_create_service_failure_body')
      );
      setIsCreateServiceLoading(false);
    }
    setIsCreateServiceLoading(false);
  }, [serviceData, createServiceSuccess, createServiceErrorMsg]);

  useEffect(() => {
    if (viewStatus && createServiceSuccess) {
      dispatch(setShowServiceExistWarning(false));
      // Always land on service details after create. If the user came from journey designer, sessionStorage
      // still holds the return context until approval — ServiceOperationPatchMonitor redirects to the journey
      // when status is patched to APPROVED (submit for review → approve).
      const normalizedViewPath = [
        `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}`,
        serviceData?.serviceName,
        serviceData?.version,
        SERVICE_MODE.VIEW
      ].filter(Boolean).join('/');
      history(normalizedViewPath, { state: { ...location.state, status: STATUS_TYPES.DRAFT, version: serviceData?.version, cloneServiceName: '' } });
    }
  }, [viewStatus]);

  return { callCreateService, isCreateServiceLoading };
};
